import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { BadgeCheck, Check, ChevronDown, ExternalLink, Gift, LucideAngularModule, Search, Star, X, XCircle } from 'lucide-angular';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { Observable, Subject, debounceTime, distinctUntilChanged, switchMap, takeUntil, startWith, catchError, of, map } from 'rxjs';
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'app-searchable-select',
  imports: [ReactiveFormsModule, CommonModule, LucideAngularModule, FormsModule],
  templateUrl: './searchable-select.component.html',
  styleUrl: './searchable-select.component.css',
  standalone: true,
})
export class SearchableSelectComponent extends Destroyer implements OnInit, OnChanges {
  @Input() control?: FormControl = undefined;
  @Input() searchFunction!: (term: string, page?: number) => Observable<{ options: { value: any; label: string; meta?: any }[], hasMore: boolean }>;
  @Input() initialOptions?: { value: any; label: string; meta?: any }[];
  @Input() label!: string;
  @Input() placeholder?: string;
  @Input() showMeta = false;
  @Input() multiple = false;
  @Input() height: string | number = '11';
  @Input() dropdownMaxHeight = '28rem';
  @Input() searchPlaceholder = 'Search...';
  @Input() required = false;
  @Input() minSearchLength = 2;
  @Input() enableInfiniteScroll = true;
  @Input() autofocus = false;

  @Input() allowCreate = false;
  @Input() createPlaceholder = '';
  @Input() enableOptionLinks = false;
  @Input() optionLinkGenerator?: (option: { value: any; label: string }) => string | null;
  @Output() optionCreate = new EventEmitter<{ name: string }>();
  @Output() optionLinkClick = new EventEmitter<{ option: { value: any; label: string }, event: Event }>();

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  protected readonly ChevronDownIcon = ChevronDown;
  protected readonly XIcon = X;
  protected readonly XCircleIcon = XCircle;
  protected readonly SearchIcon = Search;
  protected readonly ExternalLinkIcon = ExternalLink;
  protected readonly StarIcon = Star;
  protected readonly GiftIcon = Gift;
  protected readonly PiggyBankIcon = BadgeCheck;
  protected readonly CheckIcon = Check;
  protected loading = false;
  protected searchLoading = false;
  protected loadingMore = false;

  options: {
    id: number;
    value: any;
    label: string;
    selected: boolean;
    meta?: any;
  }[] = [];

  allOptions: {
    id: number;
    value: any;
    label: string;
    selected: boolean;
    meta?: any;
  }[] = [];

  isOpen = false;
  private clickListener: (() => void) | null = null;
  newOption: string = '';
  creating = false;
  searchQuery: string = '';
  private searchSubject = new Subject<string>();
  private optionCache = new Map<string, { options: { value: any; label: string; meta?: any }[], hasMore: boolean }>();
  
  // Infinite scroll properties
  private currentPage = 1;
  protected hasMore = false;
  private isInitialLoad = true;

  constructor(
    private elRef: ElementRef,
    private renderer: Renderer2,
  ) {
    super();

    // Prevent dropdown from closing when interacting with filter buttons inside parent container
    this.renderer.listen('document', 'click', (event: Event) => {
      const target = event.target as HTMLElement;
      if (target && target.closest('.product-filter-buttons')) {
        event.stopPropagation();
      }
    });
  }

  ngOnInit() {
    // Set up search debouncing
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => {
        // Reset pagination when search term changes
        this.currentPage = 1;
        this.hasMore = false;
        this.isInitialLoad = term.length === 0;
        
        if (term.length < this.minSearchLength && term.length > 0) {
          return of({ options: [], hasMore: false });
        }
        
        // For empty search, merge initial options with the first page results
        if (term.length === 0 && this.initialOptions && this.initialOptions.length > 0) {
          this.searchLoading = true;
          return this.searchFunction(term, this.currentPage).pipe(
            map(result => ({
              options: this.mergeInitialOptions(result.options),
              hasMore: result.hasMore,
            })),
            catchError(error => {
              console.error('Search error:', error);
              return of({ options: this.initialOptions ?? [], hasMore: false });
            })
          );
        }
        
        // Check cache first for search terms
        const cacheKey = `${term}-${this.currentPage}`;
        if (this.optionCache.has(cacheKey) && this.currentPage === 1) {
          return of(this.optionCache.get(cacheKey)!);
        }
        
        this.searchLoading = true;
        return this.searchFunction(term, this.currentPage).pipe(
          catchError(error => {
            console.error('Search error:', error);
            return of({ options: [], hasMore: false });
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(result => {
      this.searchLoading = false;
      this.hasMore = result.hasMore;
      
      // Cache the results if it was a search
      if (this.searchQuery.length >= this.minSearchLength) {
        const cacheKey = `${this.searchQuery}-${this.currentPage}`;
        this.optionCache.set(cacheKey, result);
      }
      
      // For initial load or new search, replace options
      if (this.currentPage === 1) {
        this.setOptions(result.options);
      } else {
        // For pagination, append options
        this.appendOptions(result.options);
      }
    });

    // Listen for control value changes
    if (this.control) {
      this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.updateSelectionState();
      });
    }

    // Load initial options
    if (this.initialOptions) {
      this.setOptions(this.initialOptions);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialOptions'] && this.initialOptions) {
      this.setOptions(this.initialOptions);
    }
  }

  private setOptions(choices: { value: any; label: string; meta?: any }[]) {
    const existingOptionsByValue = new Map(this.allOptions.map((option) => [option.value, option]));
    const choiceValues = new Set<any>();

    const mappedOptions = choices.map((choice) => {
      choiceValues.add(choice.value);
      const existingOption = existingOptionsByValue.get(choice.value);

      if (existingOption) {
        existingOption.label = choice.label;
        existingOption.selected = this.isSelectedInControl(choice.value);
        existingOption.meta = (choice as any).meta ?? existingOption.meta;
        return existingOption;
      }

      return {
        value: choice.value,
        label: choice.label,
        selected: this.isSelectedInControl(choice.value),
        meta: (choice as any).meta,
        id: Math.floor(Math.random() * 1000000),
      };
    });

    // Preserve any already-selected options so a new search does not drop them.
    const selectedButMissing = this.allOptions.filter(
      (option) => option.selected && !choiceValues.has(option.value),
    );

    this.options = [...mappedOptions];
    this.allOptions = [...mappedOptions, ...selectedButMissing];
  }

  private mergeInitialOptions(choices: { value: any; label: string; meta?: any }[]) {
    const baseChoices: { value: any; label: string; meta?: any }[] =
      this.options.length > 0
        ? this.options.map((option) => ({ value: option.value, label: option.label, meta: option.meta }))
        : (this.initialOptions ?? []);

    if (baseChoices.length === 0) {
      return choices;
    }

    const incomingByValue = new Map(choices.map((choice) => [choice.value, choice]));
    const merged: { value: any; label: string; meta?: any }[] = [];
    const seen = new Set<any>();

    baseChoices.forEach((choice) => {
      const incoming = incomingByValue.get(choice.value);
      merged.push(incoming ?? choice);
      seen.add(choice.value);
    });

    choices.forEach((choice) => {
      if (!seen.has(choice.value)) {
        merged.push(choice);
      }
    });

    return merged;
  }

  private appendOptions(choices: { value: any; label: string; meta?: any }[]) {
    const newOptions = choices.map((choice) => {
      return {
        value: choice.value,
        label: choice.label,
        selected: this.isSelectedInControl(choice.value),
        meta: (choice as any).meta,
        id: Math.floor(Math.random() * 1000000),
      };
    });
    
    // Add to both allOptions and options
    this.allOptions.push(...newOptions);
    this.options.push(...newOptions);
    this.loadingMore = false;
  }

  private updateSelectionState() {
    this.options.forEach((option) => {
      option.selected = this.isSelectedInControl(option.value);
    });
    this.allOptions.forEach((option) => {
      option.selected = this.isSelectedInControl(option.value);
    });
  }

  toggleOpen() {
    if (this.loading || this.searchLoading) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.addClickListener();
      // Reset search when opening
      this.searchQuery = '';
      this.currentPage = 1;
      this.hasMore = false;
      this.triggerSearch('');
      
      // Focus search input if autofocus is enabled
      if (this.autofocus) {
        setTimeout(() => {
          this.searchInput?.nativeElement?.focus();
        }, 0);
      }
    } else {
      this.removeClickListener();
    }
  }

  select(id: number) {
    const option = this.allOptions.find((opt) => opt.id === id);
    if (option) {
      if (this.multiple) {
        option.selected = !option.selected;
      } else {
        this.allOptions.forEach((opt) => {
          opt.selected = false;
        });
        option.selected = true;
      }
      // Update the filtered options as well
      this.options.forEach((opt) => {
        if (opt.id === id) {
          opt.selected = option.selected;
        } else if (!this.multiple) {
          opt.selected = false;
        }
      });
    }
    if (!this.multiple) {
      this.toggleOpen();
    }

    this.control?.setValue(this.selectedValues());
    this.control?.markAsDirty();
  }

  deselect(id: number) {
    const optionAll = this.allOptions.find((opt) => opt.id === id);
    if (optionAll) {
      optionAll.selected = false;
    }
    const optionFiltered = this.options.find((opt) => opt.id === id);
    if (optionFiltered) {
      optionFiltered.selected = false;
    }
    this.control?.setValue(this.selectedValues());
    this.control?.markAsDirty();
  }

  clearAll() {
    // Prevent event propagation to avoid opening dropdown
    event?.stopPropagation();

    // Deselect all options
    this.allOptions.forEach((opt) => (opt.selected = false));
    this.options.forEach((opt) => (opt.selected = false));

    // Update control value
    if (this.multiple) {
      this.control?.setValue([]);
    } else {
      this.control?.setValue(null);
    }
    this.control?.markAsDirty();
  }

  hasSelectedOptions(): boolean {
    return this.selectedOptions().length > 0;
  }

  selectedValues() {
    if (this.multiple) {
      return this.selectedOptions().map((option) => option.value);
    } else {
      return this.selectedOptions().map((option) => option.value)[0];
    }
  }

  selectedOptions() {
    return this.allOptions.filter((option) => option.selected);
  }

  private isSelectedInControl(value: any) {
    if (!this.control || !this.control.value) {
      return false;
    }
    if (this.multiple) {
      return this.control.value.includes(value);
    }
    return this.control.value === value;
  }

  createOption() {
    this.creating = true;
    this.optionCreate.emit({ name: this.newOption });
  }

  onSearchChange() {
    this.triggerSearch(this.searchQuery);
  }

  private triggerSearch(term: string) {
    this.searchSubject.next(term);
  }

  onScroll(event: Event) {
    if (!this.enableInfiniteScroll || this.loadingMore || !this.hasMore) {
      return;
    }

    const element = event.target as HTMLElement;
    const threshold = 50; // Load more when 50px from bottom
    
    if (element.scrollTop + element.clientHeight >= element.scrollHeight - threshold) {
      this.loadMore();
    }
  }

  loadMore() {
    if (this.loadingMore || !this.hasMore) {
      return;
    }

    this.loadingMore = true;
    this.currentPage++;
    
    const searchTerm = this.searchQuery.length >= this.minSearchLength ? this.searchQuery : '';
    
    this.searchFunction(searchTerm, this.currentPage).pipe(
      catchError(error => {
        console.error('Load more error:', error);
        this.loadingMore = false;
        this.currentPage--; // Revert page increment on error
        return of({ options: [], hasMore: false });
      }),
      takeUntil(this.destroy$)
    ).subscribe(result => {
      this.hasMore = result.hasMore;
      this.appendOptions(result.options);
      
      // Cache the results
      const cacheKey = `${searchTerm}-${this.currentPage}`;
      this.optionCache.set(cacheKey, result);
    });
  }

  getDisplayText(): string {
    const selected = this.selectedOptions();
    if (selected.length === 0) {
      return this.placeholder || 'Select...';
    }
    if (this.multiple) {
      return selected.length === 1 ? selected[0].label : `${selected.length} selected`;
    }
    return selected[0].label;
  }

  private addClickListener() {
    this.clickListener = this.renderer.listen('document', 'click', (event: Event) => {
      if (!this.elRef.nativeElement.contains(event.target)) {
        this.isOpen = false;
        this.removeClickListener();
      }
    });
  }

  private removeClickListener() {
    if (this.clickListener) {
      this.clickListener();
      this.clickListener = null;
    }
  }

  onOptionLinkClick(option: { value: any; label: string }, event: Event) {
    event.stopPropagation(); // Prevent the option from being selected
    this.optionLinkClick.emit({ option, event });
  }

  generateOptionLink(option: { value: any; label: string }): string | null {
    if (this.optionLinkGenerator) {
      return this.optionLinkGenerator(option);
    }
    return null;
  }

  badgeTitle(badges?: string[]): string {
    if (!badges || badges.length === 0) {
      return '';
    }

    const labels: Record<string, string> = {
      minSpend: 'Counts toward minimum spend',
      qualifying: 'Counts toward £500 qualifying spend',
      offer: 'Eligible for £150 offer',
    };

    return badges
      .map((badge) => labels[badge] || badge)
      .join(' • ');
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.removeClickListener();
  }
}
